/**
 * `Annotations_Registry.ts` — port of `AI/Registry/Annotations_Registry.cs`.
 *
 * Only `create_annotation` is ported. `create_measurement`, `take_snapshot`
 * and `get_shortest_distance` stay out until they have web executors (see
 * `AIAgentToolRegistryModel.ts`).
 *
 * Deviation: Unity anchors the comment at the laser-pointer hit when the user
 * is pointing at the part. The web tracks which part is pointed at but not
 * where, so the description promises the part's centre only.
 *
 * Deviation: Unity's `create_annotation` is writer-only. Here a Commenter may
 * run it too (`commenterAllowed`), because the Comment tool already lets a
 * Commenter add the same comment by hand (user direction, 2026-10-27).
 * The comment writes also run while a workflow plays — a learner's run or an
 * author's preview — and the comment outlives it (user direction, 2026-11-02);
 * see `AIGuardrailsModel.authorizeProjectWrite`.
 *
 * Web-first (2026-10-28): `get_annotations`, `edit_annotation`,
 * `resolve_annotation` and `move_annotation`. Unity's agent can add a comment
 * but not find, change, resolve or move one. Each write is gated the way the
 * same action is by hand: resolving and moving by the Comment tool's role rule
 * (`commenterAllowed`), editing by authorship as well, which the executor
 * checks because the comment context menu greys Edit on anyone else's comment.
 */

import { AgentToolNames } from "./agentToolNames";
import {
  type AIAgentTool,
  boolParam,
  buildParams,
  stringList,
  stringParam,
} from "./AIAgentToolRegistryModel";

export function addAnnotationsTools(tools: AIAgentTool[]): void {
  tools.push(
    {
      name: AgentToolNames.CreateAnnotation,
      category: "Annotations",
      labelWhenActive: "Adding comment...",
      description:
        "Creates an annotation (also called a comment) on a part, visible to everyone in the project. Anchored where the user is pointing when they are pointing at that part, otherwise at a point of the part they can see; the bubble is placed in clear sight. Resolve targets first (see system prompt). Provide partPath, and the text in value.",
      humanDescription: "Add a text annotation or comment to a part.",
      parameters: buildParams({
        partPath: stringParam(
          `Hierarchy path from ${AgentToolNames.GetSceneContext} (e.g. '0,1,2').`,
          false,
        ),
        value: stringParam("Text content for the annotation."),
      }),
      enabled: true,
      writerOnly: true,
      commenterAllowed: true,
    },
    {
      name: AgentToolNames.GetAnnotations,
      category: "Annotations",
      labelWhenActive: "Looking through comments...",
      description:
        "Lists the comments (also called annotations) in the current scene, newest first. "
        + "Each line is `annotationId:\"<id>\": \"<text>\"` followed by its author ('you' when the current user wrote it), the part it is attached to when known, its reply count and whether it is resolved; its replies follow, indented. "
        + `Call it to find the comment the user means before ${AgentToolNames.EditAnnotation}, ${AgentToolNames.ResolveAnnotation} or ${AgentToolNames.MoveAnnotation}, and to answer questions about the scene's comments. `
        + "Match 'the comment on <part name>' against the part each line names, 'my comment' against 'by you', and 'the comment about <topic>' against the text. "
        + "Copy annotationId values verbatim into downstream tool args; never read them out. "
        + "SILENT CALL - finding the comment is your reasoning, not something to say: never announce that you are looking through the comments or set out what you will do once the list is in. Call with nothing spoken, and speak once, with the answer or the result.",
      humanDescription: "List the comments in the current scene.",
      parameters: buildParams(),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.EditAnnotation,
      category: "Annotations",
      labelWhenActive: "Editing comment...",
      description:
        "Replaces the text of an existing comment. "
        + `Only its author can edit a comment: use it on a comment ${AgentToolNames.GetAnnotations} lists as 'by you', and for anyone else's, tell the user only its author can change it. `
        + `A resolved comment must be reopened with ${AgentToolNames.ResolveAnnotation} first. `
        + `Provide annotationId from ${AgentToolNames.GetAnnotations}, and the complete new text in value - it replaces the old text, so keep any of the old wording the user did not ask to change.`,
      humanDescription: "Change the text of a comment you wrote.",
      parameters: buildParams({
        annotationId: stringParam(`Comment id from ${AgentToolNames.GetAnnotations}.`, false),
        value: stringParam("The comment's complete new text.", false),
      }),
      enabled: true,
      writerOnly: true,
      commenterAllowed: true,
    },
    {
      name: AgentToolNames.ResolveAnnotation,
      category: "Annotations",
      labelWhenActive: "Resolving comment...",
      description:
        "Resolves comments, or reopens resolved ones with resolved=false. A resolved comment leaves the scene view and stays in the comments list. "
        + `Provide annotationIds from ${AgentToolNames.GetAnnotations} - all of them in one call when the user asks about more than one.`,
      humanDescription: "Resolve or reopen comments.",
      parameters: buildParams({
        annotationIds: stringList(`Comment ids from ${AgentToolNames.GetAnnotations}.`, false),
        resolved: boolParam("True resolves the comments; false reopens them. Default true.", true),
      }),
      enabled: true,
      writerOnly: true,
      commenterAllowed: true,
    },
    {
      name: AgentToolNames.MoveAnnotation,
      category: "Annotations",
      labelWhenActive: "Moving comment...",
      description:
        `Moves a comment onto a part: attaches it to a point of the part the user can see and places its bubble in clear sight, the way ${AgentToolNames.CreateAnnotation} places a new one. `
        + "To bring a comment into view without changing its part, pass the part it is on now. "
        + "Resolve the part first (see system prompt). "
        + `Provide annotationId from ${AgentToolNames.GetAnnotations}, and partPath.`,
      humanDescription: "Move a comment onto a part.",
      parameters: buildParams({
        annotationId: stringParam(`Comment id from ${AgentToolNames.GetAnnotations}.`, false),
        partPath: stringParam(
          `Hierarchy path from ${AgentToolNames.GetSceneContext} (e.g. '0,1,2').`,
          false,
        ),
      }),
      enabled: true,
      writerOnly: true,
      commenterAllowed: true,
    },
  );
}
